/**
 * This module exports Scalyr-defined pure functions related to arrays.
 */
import { isString, take, takeRight } from 'lodash';
import { wildcardMatch } from 'common/util/string';

/**
 * Returns a new array of a given size.
 *
 * If sourceArray.length > size and changeLeft = false, elements will be removed from the end of sourceArray.
 * If sourceArray.length < size and changeLeft = false, elements will be added to the end of sourceArray.
 * If sourceArray.length > size and changeLeft = true, elements will be removed from the start of sourceArray.
 * If sourceArray.length < size and changeLeft = true, elements will be added to the start of sourceArray.
 *
 * @param sourceArray     The original array
 * @param size            The size of the new array
 * @param filler          The filler value
 * @param changeLeft      Determines which side of the array will be padded or trimmed
 *
 * @returns               A new array with length equal to the size parameter
 */
export function createSizedArray<T>({
  changeLeft = false,
  filler,
  size,
  sourceArray = [],
}: {
  changeLeft?: boolean;
  filler?: T;
  size: number;
  sourceArray?: ReadonlyArray<T>;
}): Array<T> {
  if (changeLeft) {
    return sourceArray.length < size
      ? Array(size - sourceArray.length)
          .fill(filler)
          .concat(sourceArray)
      : takeRight(sourceArray, size);
  }
  return sourceArray.length < size
    ? sourceArray.concat(Array(size - sourceArray.length).fill(filler))
    : take(sourceArray, size);
}

/**
 * Returns a new array with the given item moved left one spot in the array.
 *
 * Example: ([1, 2, 3], 2) => [2, 1, 3]
 *
 * @param arr     array
 * @param item    item to move left
 *
 * @return        new array with the item moved
 */
export function moveLeft<T>(arr: ReadonlyArray<T>, item: T): Array<T> {
  const newArray = arr.slice();
  const idx = arr.indexOf(item);
  if (idx <= 0) {
    return newArray;
  }
  [newArray[idx], newArray[idx - 1]] = [newArray[idx - 1], newArray[idx]];

  return newArray;
}

/**
 * Returns a new array with the given item moved right one spot in the array.
 *
 * Example: ([1, 2, 3], 2) => [1, 3, 2]
 *
 * @param arr     array
 * @param item    item to move right
 *
 * @return        new array with the item moved
 */
export function moveRight<T>(arr: ReadonlyArray<T>, item: T): Array<T> {
  const newArray = arr.slice();
  const idx = arr.indexOf(item);
  if (idx === -1 || idx === arr.length - 1) {
    return newArray;
  }
  [newArray[idx], newArray[idx + 1]] = [newArray[idx + 1], newArray[idx]];

  return newArray;
}

/**
 * Return a lookup hash where each value of the given array is set to true.
 *
 * @param array    source array
 * @return object whose keys are the array values and whose values are all true
 */
export const arrayToHash = (array: ReadonlyArray<string>): Record<string, boolean> => {
  const hash: Record<string, boolean> = {};
  array.forEach((item) => {
    hash[item] = true;
  });
  return hash;
};

/**
 * Filter a list using the given string, with wildcard support. In the simplest use, a list of strings is filtered by
 * another string. Optionally, objects can be filtered by supplying a function that pulls target strings out of them.
 *
 * @param list      source array
 * @param filter    filter to use
 * @param limit     maximum size of filtered list; 0 or undefined means no limit
 * @param fetcher   function to retrieve strings out of an object
 *
 * @return {array} filtered list with only the matching items
 */
export function filterList<T>(
  list: ReadonlyArray<T> = [],
  filter: string,
  limit?: number,
  fetcher?: (obj: T) => Array<string>,
): Array<T> {
  if (list.length === 0) {
    return [];
  }

  const search = filter ? filter.trim().toLowerCase() : '';
  if (!search || search === '*') {
    return limit && list.length >= limit ? list.slice(0, limit) : list.slice();
  }

  // we don't just call filter() on the list here so that we can avoid unnecessary matching if there's a limit
  const result = [];
  for (let i = 0; i < list.length; i += 1) {
    const item = list[i];
    const strings = fetcher ? fetcher(item) : [item];
    if (strings && strings.length > 0) {
      for (let s = 0; s < strings.length; s += 1) {
        const str = strings[s];
        if (isString(str) && wildcardMatch(str, search)) {
          result.push(item);
          break;
        }
      }
    }
    if (limit && result.length === limit) {
      break;
    }
  }

  return result;
}

/**
 * function that compares strings in a case-insensitive manner.
 *
 * @param a    a string
 * @param b    another string
 *
 * @return numeric comparison result 0, 1, or -1
 */
export const compareIgnoreCase = (a: string, b: string): number => {
  const aLower = a.toLowerCase();
  const bLower = b.toLowerCase();
  if (aLower < bLower) {
    return -1;
  }
  if (aLower > bLower) {
    return 1;
  }
  return 0;
};

/**
 * Return the length of the longest string in the list.
 *
 * @param list    a list of strings
 * @returns the length of the longest string
 */
export const getMaxLength = (list: string[]): number => {
  const longest = list.reduce((acc, item) => (acc.length > item.length ? acc : item), '');
  return longest.length;
};
